import { motion } from 'framer-motion';

function AboutHero() {
  return (
    <section className="relative mx-auto max-w-6xl overflow-hidden px-6 pb-32 pt-36">
      {/* Subtle background */}
      <div className="absolute inset-0 -z-10">
        {/* Soft radial glow */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_60%_30%,rgba(99,102,241,0.16),transparent_60%)]" />
        {/* Fine grid */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(99,102,241,0.06)_1px,transparent_1px),linear-gradient(to_bottom,rgba(99,102,241,0.06)_1px,transparent_1px)] bg-[size:72px_72px]" />
      </div>

      <div className="mx-auto max-w-6xl text-center">
        {/* Main heading */}
        <motion.h1
          className="mb-6 text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl lg:text-6xl dark:text-white/90"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        >
          <motion.span
            className="block text-gray-900 dark:text-gray-100"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
          >
            Great Engineering
          </motion.span>
          <motion.span
            className="mt-2 block bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4, ease: 'easeOut' }}
          >
            for a Great Future
          </motion.span>
        </motion.h1>

        {/* Subheading */}
        <motion.p
          className="mx-auto max-w-3xl text-lg leading-relaxed text-gray-600 sm:text-xl dark:text-gray-300"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6, ease: 'easeOut' }}
        >
          We build reliable, scalable, and intelligent systems that solve real problems. Our focus
          is simple: strong fundamentals, thoughtful design, and engineering that lasts.
        </motion.p>

        {/* Value highlights */}
        <motion.div
          className="mt-10 flex flex-wrap justify-center gap-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8, ease: 'easeOut' }}
        >
          {['Engineering Excellence', 'Long-Term Thinking', 'Built for Real Scale'].map(
            (value, index) => (
              <motion.div
                key={value}
                className="rounded-full bg-white/80 px-5 py-2 text-sm font-medium text-gray-800 shadow-sm backdrop-blur dark:bg-gray-800 dark:text-gray-100"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{
                  duration: 0.5,
                  delay: 1 + index * 0.1,
                  ease: 'easeOut',
                }}
                whileHover={{ scale: 1.05, y: -2 }}
              >
                {value}
              </motion.div>
            ),
          )}
        </motion.div>
      </div>
    </section>
  );
}

function CeoMessage() {
  return (
    <section className="relative mx-auto max-w-6xl px-6 py-28">
      <div className="grid items-center gap-16 lg:grid-cols-2">
        {/* CEO Image */}
        <div className="relative">
          <div className="absolute -inset-4 -z-10 rounded-full bg-gradient-to-tr from-indigo-400/20 via-purple-400/20 to-pink-400/20 blur-2xl" />
          <img
            src="/Images/team/lakindu.png"
            alt="Lakindu Widuranga Alwis is the CEO of Akwid Labs, a technology company specializing in AI, Telecom, Data Engineering, Software Engineering, FinTech, Designing and Marketing. With a strong background in software architecture and engineering leadership, Lakindu has been instrumental in driving innovation and delivering impactful solutions for clients across various industries."
            className="w-full max-w-md rounded-3xl"
          />
        </div>

        {/* CEO Content */}
        <div>
          <span className="mb-3 block text-sm font-medium tracking-wide text-indigo-500">
            Message from the CEO
          </span>

          <h2 className="mb-6 text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl dark:text-gray-100">
            Building Technology That Truly Matters
          </h2>

          <div className="space-y-5 text-base leading-relaxed text-gray-700 dark:text-gray-300">
            <p>
              Engineering is not about chasing trends or shipping features as fast as possible. It
              is about making deliberate decisions, understanding real problems, and building
              systems that people can rely on for years.
            </p>

            <p>
              From the beginning, our goal has been simple. Create technology that is practical,
              scalable, and honest in its design. We believe great software comes from strong
              fundamentals, disciplined execution, and teams that care deeply about quality.
            </p>

            <p>
              As industries evolve with AI, data, and automation, our responsibility is to build
              systems that empower businesses without adding unnecessary complexity. That is the
              standard we hold ourselves to every day.
            </p>
          </div>

          {/* CEO Signature */}
          <div className="mt-8">
            <p className="font-semibold text-gray-900 dark:text-gray-100">
              Lakindu Widuranga Alwis
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">CEO & Chief Architect</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function CompanyTimeline() {
  const timeline = [
    {
      year: '2018',
      title: 'A Dream Begins',
      description:
        'A young school student named Lakindu set out to build a gaming company, driven by curiosity and a love for creating digital worlds.',
    },
    {
      year: '2020',
      title: 'Learning Through Failure',
      description:
        'After experimenting with multiple ideas and facing setbacks in gaming, the focus shifted toward offering software services and solving real business problems.',
    },
    {
      year: '2021',
      title: 'Entering the AI Space',
      description:
        'We began exploring artificial intelligence seriously, studying data systems, machine learning, and production-ready architectures.',
    },
    {
      year: '2023',
      title: 'First International AI Project',
      description:
        'Secured our first major AI project from Australia, validating our technical direction and engineering standards.',
    },
    {
      year: '2024',
      title: 'Year of Transformation',
      description:
        'We Rebranded the company as Akwid Labs. Expanded services across the USA, New Zealand, Australia, and Sri Lanka, while entering the FinTech domain with secure, scalable systems.',
    },
    {
      year: '2025',
      title: 'Telco Industry Entry',
      description:
        'Officially stepped into the telecommunications industry and collaborated with partners in Switzerland on advanced engineering solutions.',
    },
    {
      year: '2026',
      title: 'Scaling Across Industries',
      description:
        'Rapid growth across multiple industries, powered by a best-in-class engineering team focused on reliability, performance, and long-term value.',
    },
  ];

  return (
    <section className="relative mx-auto max-w-5xl px-6 py-28">
      {/* Header */}
      <motion.div
        className="mb-20 text-center"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
        viewport={{ once: true }}
      >
        <motion.span
          className="mb-3 block text-sm font-medium tracking-wide text-indigo-500"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2, ease: 'easeOut' }}
          viewport={{ once: true }}
        >
          Our Journey
        </motion.span>
        <motion.h2
          className="text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl dark:text-gray-100"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4, ease: 'easeOut' }}
          viewport={{ once: true }}
        >
          Everything Started With a Dream
        </motion.h2>
        <motion.p
          className="mx-auto mt-4 max-w-2xl text-lg text-gray-600 dark:text-gray-300"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6, ease: 'easeOut' }}
          viewport={{ once: true }}
        >
          A journey shaped by curiosity, failures, learning, and disciplined engineering.
        </motion.p>
      </motion.div>

      {/* Timeline */}
      <div className="relative">
        {/* Vertical Line */}
        <motion.div
          className="absolute left-4 top-0 h-full w-px bg-gradient-to-b from-indigo-400/40 via-purple-400/40 to-pink-400/40 sm:left-1/2 sm:-translate-x-1/2"
          initial={{ scaleY: 0 }}
          whileInView={{ scaleY: 1 }}
          transition={{ duration: 1.5, ease: 'easeOut' }}
          viewport={{ once: true }}
          style={{ originY: 0 }}
        />

        <div className="space-y-16">
          {timeline.map((item, index) => (
            <motion.div
              key={item.year}
              className={`relative flex flex-col gap-6 sm:flex-row ${
                index % 2 === 0 ? 'sm:flex-row-reverse' : ''
              }`}
              initial={{ opacity: 0, x: index % 2 === 0 ? 50 : -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{
                duration: 0.8,
                delay: index * 0.2,
                ease: 'easeOut',
              }}
              viewport={{ once: true, margin: '-50px' }}
            >
              {/* Spacer */}
              <div className="hidden sm:block sm:w-1/2" />

              {/* Content */}
              <motion.div
                className="relative rounded-2xl bg-white/90 p-8 shadow-lg backdrop-blur-xl sm:w-1/2 dark:bg-gray-800"
                whileHover={{
                  scale: 1.02,
                  y: -5,
                  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
              >
                {/* Dot */}
                <motion.span
                  className="absolute -left-[9px] top-8 h-4 w-4 rounded-full bg-gradient-to-r from-indigo-400 to-purple-400 sm:left-auto sm:right-[-9px]"
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  transition={{
                    duration: 0.5,
                    delay: index * 0.2 + 0.3,
                    ease: 'easeOut',
                  }}
                  viewport={{ once: true }}
                />

                <motion.span
                  className="text-sm font-semibold text-indigo-500"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{
                    duration: 0.4,
                    delay: index * 0.2 + 0.4,
                    ease: 'easeOut',
                  }}
                  viewport={{ once: true }}
                >
                  {item.year}
                </motion.span>

                <motion.h3
                  className="mt-2 text-xl font-bold text-gray-900 dark:text-gray-100"
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.4,
                    delay: index * 0.2 + 0.5,
                    ease: 'easeOut',
                  }}
                  viewport={{ once: true }}
                >
                  {item.title}
                </motion.h3>

                <motion.p
                  className="mt-3 text-sm leading-relaxed text-gray-700 dark:text-gray-300"
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.4,
                    delay: index * 0.2 + 0.6,
                    ease: 'easeOut',
                  }}
                  viewport={{ once: true }}
                >
                  {item.description}
                </motion.p>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function OurValues() {
  const values = [
    {
      title: 'Engineering First',
      description:
        'We prioritize sound architecture, clean code, and long-term reliability over shortcuts, trends, or hype.',
    },
    {
      title: 'Truth Over Comfort',
      description:
        'We speak honestly about risks, limitations, and trade-offs. Clear truth beats polite uncertainty every time.',
    },
    {
      title: 'Learn Relentlessly',
      description:
        'Technology moves fast. We study, test, fail, and adapt continuously to stay ahead of real-world problems.',
    },
    {
      title: 'Build for Scale',
      description:
        'Every system is designed with growth in mind, from data flow to infrastructure and team processes.',
    },
    {
      title: 'Ownership Mentality',
      description:
        'We treat every product like it is our own. Quality, security, and outcomes are personal responsibilities.',
    },
    {
      title: 'Human-Centered Technology',
      description:
        'We build advanced systems that serve people, simplify work, and create meaningful impact.',
    },
  ];

  return (
    <section className="relative mx-auto max-w-6xl px-6 py-28">
      {/* Header */}
      <motion.div
        className="mb-16 text-center"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
        viewport={{ once: true }}
      >
        <motion.span
          className="mb-3 block text-sm font-medium tracking-wide text-indigo-500"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2, ease: 'easeOut' }}
          viewport={{ once: true }}
        >
          Our Values
        </motion.span>
        <motion.h2
          className="text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl dark:text-gray-100"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4, ease: 'easeOut' }}
          viewport={{ once: true }}
        >
          How We Think. How We Build.
        </motion.h2>
        <motion.p
          className="mx-auto mt-4 max-w-2xl text-lg text-gray-600 dark:text-gray-300"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6, ease: 'easeOut' }}
          viewport={{ once: true }}
        >
          These principles guide how we design systems, work with clients, and grow as engineers.
        </motion.p>
      </motion.div>

      {/* Values Grid */}
      <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
        {values.map((value, index) => (
          <motion.div
            key={value.title}
            className="group rounded-3xl bg-white/90 p-8 shadow-lg backdrop-blur-xl transition-all duration-300 hover:shadow-2xl dark:bg-gray-800"
            initial={{ opacity: 0, y: 40, scale: 0.9 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            transition={{
              duration: 0.8,
              delay: index * 0.15,
              ease: 'easeOut',
            }}
            viewport={{ once: true, margin: '-50px' }}
            whileHover={{
              scale: 1.03,
              y: -8,
              transition: { duration: 0.3, ease: 'easeOut' },
            }}
          >
            <motion.h3
              className="text-xl font-bold text-gray-900 dark:text-gray-100"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{
                duration: 0.5,
                delay: index * 0.15 + 0.3,
                ease: 'easeOut',
              }}
              viewport={{ once: true }}
            >
              {value.title}
            </motion.h3>
            <motion.p
              className="mt-4 text-sm leading-relaxed text-gray-700 dark:text-gray-300"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.5,
                delay: index * 0.15 + 0.5,
                ease: 'easeOut',
              }}
              viewport={{ once: true }}
            >
              {value.description}
            </motion.p>

            {/* Animated Accent */}
            <motion.div
              className="mt-6 h-1 rounded-full bg-gradient-to-r from-indigo-400 to-purple-400"
              initial={{ width: 0 }}
              whileInView={{ width: 40 }}
              whileHover={{ width: 64 }}
              transition={{
                duration: 0.6,
                delay: index * 0.15 + 0.7,
                ease: 'easeOut',
              }}
              viewport={{ once: true }}
            />
          </motion.div>
        ))}
      </div>
    </section>
  );
}

export default function AboutUs() {
  return (
    <div className="mx-auto sm:px-6 lg:px-8">
      <AboutHero />
      <CeoMessage />
      <CompanyTimeline />
      <OurValues />
    </div>
  );
}
